import React, { useMemo, useState, useEffect } from "react";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
} from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import { apiService } from "../../../components/Config/api.service";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

export default function Comptability({ month, year, setIsJournalConfirmOpen }) {
    // --- replaced static data with API-driven state ---
    const [items, setItems] = useState([]); // { label, cost }
    const [total, setTotal] = useState(0);
    const [uniqueDepartments, setUniqueDepartments] = useState(0);
    const [uniqueAssignments, setUniqueAssignments] = useState(0);
    const [employeeCount, setEmployeeCount] = useState(0);

    const [viewBy, setViewBy] = useState("department"); // 'department' | 'assignment'
    const [highlighted, setHighlighted] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch repartition from backend when viewBy, month or year changes (or on mount)
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        // build month param as YYYY-MM if month+year provided

        const endpoint = `accounting/repartition?view_by=${viewBy}${month ? `&month=${month}` : ""}`;

        apiService.getElement(endpoint)
            .then((response) => {
                if (cancelled) return;
                const data = response.data || response;
                // API returns items as [{ label, cost }, ...]
                setItems(Array.isArray(data.items) ? data.items : []);
                setTotal(typeof data.total === "number" ? data.total : 0);
                setUniqueDepartments(typeof data.unique_departments === "number" ? data.unique_departments : 0);
                setUniqueAssignments(typeof data.unique_assignments === "number" ? data.unique_assignments : 0);
                setEmployeeCount(typeof data.employee_count === "number" ? data.employee_count : 0);
            })
            .catch((err) => {
                if (!cancelled) setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => (cancelled = true);
    }, [viewBy, month, year]);

    // Aggregate costs by key (department or assignment)
    const aggregated = useMemo(() => {
        const map = new Map();
        // incoming items have { label, cost }
        for (const e of items) {
            const key = e.label ?? (viewBy === "department" ? e.department : e.assignment) ?? "N/A";
            const cost = Number(e.cost) || 0;
            map.set(key, (map.get(key) || 0) + cost);
        }
        const aggrItems = Array.from(map.entries())
            .map(([label, cost]) => ({ label, cost }))
            .sort((a, b) => b.cost - a.cost);
        const total = aggrItems.reduce((s, it) => s + it.cost, 0);
        return { items: aggrItems, total };
    }, [items, viewBy]);

    const pieData = {
        labels: aggregated.items.map((i) => i.label),
        datasets: [
            {
                data: aggregated.items.map((i) => i.cost),
                backgroundColor: [
                    "#0C5B6D",
                    "#FB923C",
                    "#06B6D4",
                    "#EF4444",
                    "#10B981",
                    "#6366F1",
                    "#F97316",
                    "#93C5FD",
                ],
                hoverOffset: 8,
            },
        ],
    };

    const barData = {
        labels: aggregated.items.map((i) => i.label),
        datasets: [
            {
                label: "Coût (FCFA)",
                data: aggregated.items.map((i) => i.cost),
                backgroundColor: aggregated.items.map((i, idx) =>
                    idx === 0 ? "#0C5B6D" : "#93C5FD"
                ),
            },
        ],
    };

    const pieOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "right", labels: { boxWidth: 12, padding: 8 } },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const value = ctx.parsed;
                        const total = aggregated.total || 1;
                        const pct = ((value / total) * 100).toFixed(1);
                        return `${ctx.label}: FCFA ${Number(value).toLocaleString()} (${pct}%)`;
                    },
                },
            },
        },
        onHover: (evt, elements) => {
            if (elements.length) {
                const idx = elements[0].index;
                setHighlighted(pieData.labels[idx]);
            } else {
                setHighlighted(null);
            }
        },
    };

    const barOptions = {
        maintainAspectRatio: false,
        indexAxis: "y",
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const v = (ctx.parsed && (ctx.parsed.x ?? ctx.parsed.y ?? ctx.parsed)) || 0;
                        return `FCFA ${Number(v).toLocaleString()}`;
                    }
                }
            },
        },
        scales: {
            x: { ticks: { callback: (v) => `FCFA ${Number(v).toLocaleString()}` } },
        },
        onHover: (evt, elements) => {
            if (elements && elements.length > 0) {
                const idx = elements[0].index;
                const label = aggregated.items[idx] ? aggregated.items[idx].label : null;
                setHighlighted(label);
            } else {
                setHighlighted(null);
            }
        }
    };

    const top = aggregated.items[0] || { label: "-", cost: 0 };

    // // Download aggregated data as CSV
    // const handleDownload = () => {
    //     const headers = ["label", "cost"];
    //     const rows = aggregated.items.map((it) => [it.label, it.cost]);
    //     const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    //     const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    //     const url = URL.createObjectURL(blob);
    //     const a = document.createElement("a");
    //     a.href = url;
    //     a.download = `repartition_${viewBy}.csv`;
    //     document.body.appendChild(a);
    //     a.click();
    //     a.remove();
    //     URL.revokeObjectURL(url);
    // };

    return (
        <div className="">
            <div className=" p-4 flex justify-between items-center bg-white rounded shadow mb-4">
                <div className="flex items-center justify-center">
                    <div className="flex gap-2">
                        <button
                            onClick={() => setViewBy("department")}
                            className={`px-3 py-2 rounded ${
                                viewBy === "department"
                                    ? "bg-[--color-primary] text-white"
                                    : "bg-gray-200 text-gray-700"
                            }`}
                        >
                            Département
                        </button>
                        <button
                            onClick={() => setViewBy("assignment")}
                            className={`px-3 py-2 rounded ${
                                viewBy === "assignment"
                                    ? "bg-[--color-primary] text-white"
                                    : "bg-gray-200 text-gray-700"
                            }`}
                        >
                            Affectation
                        </button>
                    </div>
                </div>

                <div className="">
                    <button
                        onClick={() => setIsJournalConfirmOpen(true)}
                        className="px-3 py-2 bg-[--color-primary] text-white rounded shadow hover:bg-[--color-orange]"
                        title={`Télécharger les données (${viewBy})`}
                    >
                        Télécharger Journal
                    </button>
                </div>
            </div>
            {/* Top KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-white shadow rounded p-4 flex flex-col justify-center">
                    <div className="text-sm text-gray-500">Employés</div>
                    <div className="text-2xl font-bold">{employeeCount.toLocaleString()}</div>
                </div>

                <div className="bg-white shadow rounded p-4 flex flex-col justify-center">
                    <div className="text-sm text-gray-500">Coût total</div>
                    <div className="text-2xl font-bold">FCFA {aggregated.total.toLocaleString()}</div>
                </div>

                <div className="bg-white shadow rounded p-4 flex flex-col justify-center">
                    <div className="text-sm text-gray-500">{viewBy === "department" ? "Département" : "Affectation"}</div>
                    <div className="text-2xl font-bold">{viewBy === "department" ? uniqueDepartments : uniqueAssignments}</div>
                </div>

                {/* Dynamic top card for current view */}
                <div className="bg-white shadow rounded p-4 flex flex-col justify-center">
                    <div className="text-sm text-gray-500">
                        Top {viewBy === "department" ? "Département" : "Affectation"}
                    </div>
                    <div className="text-2xl font-bold">{top.label}</div>
                    <div className="text-sm text-gray-600">FCFA {top.cost.toLocaleString()}</div>
                </div>
            </div>

            {/* Ensure columns stretch to the same height and match on md+ */}
            <div className="grid grid-cols-4 gap-4 items-stretch min-h-[520px]">
                {/* Left column: details list */}
                <div className="bg-white shadow rounded p-4 flex-1">
                    <h4 className="font-medium mb-2">Détails</h4>
                    <div className="space-y-2 max-h-[450px] overflow-auto">
                        {aggregated.items.map((it) => {
                            const pct = ((it.cost / aggregated.total) * 100 || 0).toFixed(1);
                            const isHighlighted = highlighted === it.label;
                            return (
                                <div
                                    key={it.label}
                                    className={`flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer ${
                                        isHighlighted ? "bg-indigo-50" : ""
                                    }`}
                                    onMouseEnter={() => setHighlighted(it.label)}
                                    onMouseLeave={() => setHighlighted(null)}
                                >
                                    <div className="flex flex-col">
                                        <div className="text-sm font-medium">{it.label}</div>
                                        <div className="text-xs text-gray-500">{pct}%</div>
                                    </div>
                                    <div className="text-sm font-semibold">FCFA {it.cost.toLocaleString()}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Right column: Charts */}
                <div className="col-span-3 md:gap-4 w-full h-full flex flex-col">
                    <div className="bg-white shadow rounded p-4 h-full w-full flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold">Répartition — {viewBy === "department" ? "Département" : "Affectation"}</h3>
                            <div className="text-sm text-gray-500">Hover pour mettre en avant</div>
                        </div>

                        {/* Chart area: side-by-side on md+, stacked on small screens; larger heights */}
                        <div className="flex flex-col md:flex-row gap-6 items-stretch flex-1">
                            <div className="md:w-1/2 h-96 w-full">
                                <Pie data={pieData} options={pieOptions} />
                            </div>
                            <div className="md:w-1/2 h-96 w-full">
                                <Bar data={barData} options={barOptions} />
                            </div>
                        </div>
                     </div>
                 </div>
             </div>
         </div>
     );
}
